import { useDeviceFilterValue } from "elements";
import {
  Device,
  InsightsDeviceActionQuery,
  InsightsDeviceActionQueryVariables,
  useInsightsDeviceActionQuery,
  useSupportsUsbOnboardingQuery,
  VersionBucketFragment
} from "generated/graphql";
import { CombinedError } from "@urql/core";
import { constructFilterList } from "utilities/filters/deviceFilters";
import { nameof } from "utilities/types";
import { useActionsFilter } from "./QueryParam";
import { DEVICE_ACTION_TYPES } from "Constants";
import { useTenantId } from "polyrouter/SelectedTenantProvider";

type Actions = "OFFLINE_DEVICES" | "AVAILABLE_UPDATES" | "SITE_UNASSIGNED";

export type UseDeviceAction = {
  locations: Array<string | null>;
  model: string;
  count: number;
  action: Actions;
  version?: string | null;
};
type OfflineDevices = InsightsDeviceActionQuery["offlineDevices"];
type ModelBuckets = OfflineDevices[number]["buckets"];
type ModelBucket = ModelBuckets[number];

type UpdatableDevices = InsightsDeviceActionQuery["availableUpdates"];
type UpdatableBucket = UpdatableDevices[number]["buckets"][number];
type VersionBucket = VersionBucketFragment["buckets"][number];
type LocationBucket = Pick<UseDeviceAction, "count" | "model" | "version"> &
  Pick<ModelBucket, "sites">;

type SiteUnassignedDevices = InsightsDeviceActionQuery["siteUnassigned"];

export const responseToDeviceData = (
  data: OfflineDevices,
  modelsToFilterOut: string[]
): Array<UseDeviceAction> => {
  return data
    .flatMap(e => e.buckets)
    .filter(
      e => e.__typename === "KeyBucket" || e.__typename === "FieldMissingBucket"
    )
    .map<UseDeviceAction>((e: ModelBucket) => {
      const locations = e.sites
        .flatMap(site => site.buckets)
        .map(site => {
          if (site.__typename === "KeyBucket" && site.site) {
            return site.site;
          }
          return null;
        })
        .reduce((set, siteName) => set.add(siteName), new Set<string | null>());
      const count = e.modelCount;
      let model = "";
      if (e.__typename === "KeyBucket") {
        model = e.model;
      }
      return {
        locations: Array.from(locations),
        count,
        model,
        action: "OFFLINE_DEVICES"
      };
    })
    .filter(action => !modelsToFilterOut.includes(action.model))
    .reduce(
      (acc: Array<UseDeviceAction>, { model, count, action, locations }) => {
        const repeat = acc.findIndex(it => it.model === model);
        if (repeat >= 0) {
          const prev = acc[repeat];
          acc[repeat] = {
            model,
            action,
            locations: [...locations, ...prev.locations],
            count: count + prev.count
          };
        } else {
          acc.push({ model, count, action, locations });
        }
        return acc;
      },
      []
    )
    .map(({ model, count, action, locations }) => ({
      locations: Array.from(new Set(locations)),
      count,
      model,
      action
    }));
};

export const responseToUpdatesAvailableData = (
  data: UpdatableDevices
): Array<UseDeviceAction> => {
  return data
    .flatMap(e => e.buckets)
    .filter(
      e => e.__typename === "KeyBucket" || e.__typename === "FieldMissingBucket"
    )
    .map((e: UpdatableBucket) => {
      return e.updateVersions
        .flatMap(v => v.buckets)
        .map((v: VersionBucket) => {
          let count = 0;
          if (v.__typename === "KeyBucket" && v.versionCount) {
            count = v.versionCount;
          }
          let model = "";
          if (e.__typename === "KeyBucket") {
            model = e.model;
          }
          let version = "";
          if (v.__typename === "KeyBucket" && v.updateVersion) {
            version = v.updateVersion;
          }
          let sites: any = [];
          if (v.__typename === "KeyBucket" && v.sites) {
            sites = v.sites;
          }
          return {
            count,
            model,
            version,
            sites
          };
        });
    })
    .flatMap(data => data)
    .map((e: LocationBucket) => {
      const locations = e.sites
        .flatMap(site => site.buckets)
        .map(site => {
          if (site.__typename === "KeyBucket" && site.site) {
            return site.site;
          }
          return null;
        })
        .reduce(
          (set: any, siteName: any) => set.add(siteName),
          new Set<string | null>()
        );
      const count = e.count;
      const model = e.model;
      const version = e.version;
      return {
        locations: Array.from(locations),
        version,
        count,
        model,
        action: "AVAILABLE_UPDATES"
      };
    });
};

export const responseToSiteUnassignedData = (
  data: SiteUnassignedDevices
): Array<UseDeviceAction> => {
  const models = data
    .flatMap(e => e.buckets)
    .filter(
      e => e.__typename === "KeyBucket" || e.__typename === "FieldMissingBucket"
    )
    .map(e => {
      const name = e.__typename === "KeyBucket" ? e.model : "";
      return { name, count: e.modelCount };
    });

  const modelCount = models.reduce((acc, model) => acc + model.count, 0);
  const modelName = models.length === 1 ? models[0].name : "";

  // Collate into a single action card
  return models.length
    ? [
        {
          count: modelCount,
          model: modelName,
          action: "SITE_UNASSIGNED",
          locations: ["Unassigned"]
        }
      ]
    : [];
};

let useDeviceAction = (): {
  data: Array<UseDeviceAction>;
  fetching: boolean;
  error: CombinedError | undefined;
} => {
  const tenantId = useTenantId();
  const [filterValues] = useDeviceFilterValue();
  const [actionfilterValues] = useActionsFilter();
  const {
    data: usbData,
    fetching: usbFetching
  } = useSupportsUsbOnboardingQuery();
  const filterList = constructFilterList(filterValues);
  const selectedAction = actionfilterValues.action;
  const tenantFilter = [
    {
      field: nameof<Device>("tenantId"),
      eq: tenantId
    }
  ];
  const notConnected = {
    field: nameof<Device>("connected"),
    eq: "false"
  };
  const updateAvailable = {
    field: nameof("isUpdateAvailable"),
    eq: "true"
  };
  //filter out secondary devices
  const supportsSettings = {
    field: nameof<Device>("supportsSettings"),
    eq: "true"
  };
  const supportsSoftwareUpdate = {
    field: nameof<Device>("supportsSoftwareUpdate"),
    eq: "true"
  };
  const provisioningEnabled = {
    field: nameof<Device>("provisioningEnabled"),
    eq: "true"
  };
  const connectedFieldExists = {
    field: nameof<Device>("connected"),
    exists: true
  };
  //filter out desktop models
  const notMacOS = {
    field: nameof<Device>("hardwareModel"),
    ne: "MacOS"
  };
  const notWin32 = {
    field: nameof<Device>("hardwareModel"),
    ne: "win32"
  };
  const notLensDesktop = {
    field: nameof<Device>("hardwareModel"),
    ne: "Lens Desktop"
  };

  //////////////////////////////////
  const siteUnassigned = {
    field: nameof("site"),
    eq: "Unassigned"
  };

  const offlineFilterVariables = {
    AND: [
      notConnected,
      notMacOS,
      notWin32,
      notLensDesktop,
      {
        OR: [
          supportsSettings,
          supportsSoftwareUpdate,
          provisioningEnabled,
          connectedFieldExists
        ]
      },
      ...(tenantId ? tenantFilter : []),
      ...filterList
    ]
  };
  const availableUpdatesFilterVariables = {
    AND: [updateAvailable, ...(tenantId ? tenantFilter : []), ...filterList]
  };
  const siteUnassignedFilterVariables = {
    AND: [siteUnassigned, ...(tenantId ? tenantFilter : []), ...filterList]
  };
  const showOfflineDevices: boolean =
    !selectedAction || selectedAction === DEVICE_ACTION_TYPES.OFFLINE_DEVICES;
  const showAvailableUpdates: boolean =
    !selectedAction || selectedAction === DEVICE_ACTION_TYPES.AVAILABLE_UPDATES;
  const showUnassignedSite: boolean =
    !selectedAction || selectedAction === DEVICE_ACTION_TYPES.SITE_UNASSIGNED;

  const variables: InsightsDeviceActionQueryVariables = {
    offlineFilter: offlineFilterVariables,
    availableUpdatesFilter: availableUpdatesFilterVariables,
    siteUnassignedFilter: siteUnassignedFilterVariables,
    showOfflineDevices: showOfflineDevices,
    showAvailableUpdates: showAvailableUpdates,
    showUnassignedSite: showUnassignedSite
  };

  const {
    data,
    error,
    fetching: insightsFetching
  } = useInsightsDeviceActionQuery({ variables });

  let offlineActions: Array<UseDeviceAction> = [];
  let updateActions: Array<UseDeviceAction> = [];
  let siteUnassignedActions: Array<UseDeviceAction> = [];
  const fetching = insightsFetching || usbFetching;

  // Get an array of all the names of USB supported devices, that have at least 1 sku.
  const listOfUSBSupportedDevices =
    usbData?.hardwareModels
      .filter(model => (model.skus ? model.skus.length > 0 : false))
      .map(model => model.name) ?? [];
  if (data && usbData && !fetching) {
    offlineActions = data.offlineDevices
      ? responseToDeviceData(data.offlineDevices, listOfUSBSupportedDevices)
      : [];
    updateActions = data.availableUpdates
      ? responseToUpdatesAvailableData(data.availableUpdates)
      : [];
    siteUnassignedActions = data.siteUnassigned
      ? responseToSiteUnassignedData(data.siteUnassigned)
      : [];
  }
  return {
    data: [...offlineActions, ...updateActions, ...siteUnassignedActions],
    error,
    fetching
  };
};
/**
 * Mapping the insights results is fairly beefy, so its wrapped in its own hook.
 */
export default useDeviceAction;
